
import { WatchListActiveIcon } from '@/components/icons/watchlist-active-icon';
import { WatchListIcon } from '@/components/icons/watchlist-icon';
import { AnalyticsEvent, AnalyticsPageCategories } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { useProfile } from '@/hooks/profile/profile';
import { useAddWatchListMovie, useRemoveWatchListMovie, useWatchListSimple } from '@/hooks/profile/watch-list';
import { useAuth } from '@/hooks/use-auth';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const pendingWatchListIntentKey = 'pending-watch-list-intent';
const pendingWatchListIntentParam = 'watchListIntent';
const pendingWatchListIntentTtlMs = 10 * 60 * 1000;

type PendingWatchListIntent = {
  createdAt: number;
  intentId: string;
  linkCategory?: string;
  movieId: number;
  movieTitle: string;
  returnPath: string;
};

const removeWatchListIntentParam = (): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(pendingWatchListIntentParam)) return;

  url.searchParams.delete(pendingWatchListIntentParam);
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};

const isPendingWatchListIntent = (value: unknown): value is PendingWatchListIntent => {
  if (typeof value !== 'object' || !value) return false;

  const pendingIntent = value as Record<string, unknown>;
  return typeof pendingIntent.createdAt === 'number'
    && typeof pendingIntent.intentId === 'string'
    && (pendingIntent.linkCategory === undefined || typeof pendingIntent.linkCategory === 'string')
    && typeof pendingIntent.movieId === 'number'
    && typeof pendingIntent.movieTitle === 'string'
    && typeof pendingIntent.returnPath === 'string';
};

const getPendingWatchListIntent = (): PendingWatchListIntent | null => {
  const serializedIntent = sessionStorage.getItem(pendingWatchListIntentKey);
  if (!serializedIntent) return null;

  try {
    const pendingIntent: unknown = JSON.parse(serializedIntent);
    return isPendingWatchListIntent(pendingIntent) ? pendingIntent : null;
  } catch (error) {
    if (error instanceof SyntaxError) return null;
    throw error;
  }
};

type WatchListIconComponentProps = {
  className?: string;
  dataLinkCategory?: string;
  movieId: number;
  movieTitle: string;
  showAddToWatchListText?: boolean;
};

export const WatchListIconComponent = ({
  className,
  dataLinkCategory,
  movieId,
  movieTitle,
  showAddToWatchListText,
}: WatchListIconComponentProps): React.JSX.Element => {
  const { data: profile } = useProfile();
  const { data: watchListData, isSuccess: isWatchListLoaded } = useWatchListSimple();
  const { mutateAsync: addToWatchList } = useAddWatchListMovie();
  const { mutateAsync: removeFromWatchList } = useRemoveWatchListMovie();
  const { signIn } = useAuth();
  const { trackEvent } = useDataAnalytics();
  const hasProcessedPendingAction = useRef(false);
  const [hasHydrated, setHasHydrated] = useState(false);

  const isActive = watchListData?.data?.some(item => item.movieId === movieId) ?? false;

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!profile || !isWatchListLoaded || hasProcessedPendingAction.current) return;

    const pendingIntent = getPendingWatchListIntent();
    const intentId = new URLSearchParams(window.location.search).get(pendingWatchListIntentParam);
    const isExpired = pendingIntent && Date.now() - pendingIntent.createdAt >= pendingWatchListIntentTtlMs;
    const hasMismatchedCallback = pendingIntent && intentId && intentId !== pendingIntent.intentId;
    const hasMismatchedReturnPath = pendingIntent && pendingIntent.returnPath !== window.location.pathname;

    if (!pendingIntent || isExpired || hasMismatchedCallback || hasMismatchedReturnPath) {
      sessionStorage.removeItem(pendingWatchListIntentKey);
      removeWatchListIntentParam();
      return;
    }

    if (pendingIntent.movieId !== movieId) return;

    hasProcessedPendingAction.current = true;
    sessionStorage.removeItem(pendingWatchListIntentKey);
    removeWatchListIntentParam();

    if (isActive) return;

    void addToWatchList({
      analyticsEvent: AnalyticsEvent.WATCH_LIST_ADD,
      linkCategory: pendingIntent.linkCategory,
      movieId,
      movieTitle: pendingIntent.movieTitle,
    }).then(() => {
      toast(`Added ${pendingIntent.movieTitle} to Watch List`);
    }).catch(() => {
      toast('Unable to add movie to Watch List.');
    });
  }, [addToWatchList, dataLinkCategory, isActive, isWatchListLoaded, movieId, movieTitle, profile]);

  const handleClick = async (): Promise<void> => {
    if (!profile) return;

    try {
      if (isActive) {
        await removeFromWatchList({
          analyticsEvent: AnalyticsEvent.WATCH_LIST_REMOVE,
          linkCategory: dataLinkCategory,
          movieId,
          movieTitle,
        });
        toast(`Removed ${movieTitle} from Watch List`);
      } else {
        await addToWatchList({
          analyticsEvent: AnalyticsEvent.WATCH_LIST_ADD,
          linkCategory: dataLinkCategory,
          movieId,
          movieTitle,
        });
        toast(`Added ${movieTitle} to Watch List`);
      }
    } catch  {
      toast(isActive ? 'Unable to remove the movie from Watch List.' : 'Unable to add movie to Watch List.');
    }
  };

  const handleSignInClick = (): void => {
    const intentId = crypto.randomUUID();
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set(pendingWatchListIntentParam, intentId);
    sessionStorage.setItem(pendingWatchListIntentKey, JSON.stringify({
      createdAt: Date.now(),
      intentId,
      linkCategory: dataLinkCategory,
      movieId,
      movieTitle,
      returnPath: returnUrl.pathname,
    } satisfies PendingWatchListIntent));
    trackEvent(AnalyticsEvent.USER_LOGIN, {
      linkCategory: dataLinkCategory ?? AnalyticsPageCategories.WATCH_LIST,
      linkName: 'Watch List Sign In',
    });
    signIn(`${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`);
  };

  const displayAnchorOrAction = (): React.JSX.Element => {
    if (profile && hasHydrated) {
      return (
        <Button aria-label={isActive ? `Remove ${movieTitle} from Watch List` : `Add ${movieTitle} to Watch List`}
          aria-pressed={isActive}
          className="p-0 h-auto bg-transparent hover:bg-transparent"
          data-test={isActive ? 'button-watchlist-remove' : 'button-watchlist-add'}
          variant="ghost"
          onClick={handleClick}
        >
          {getIcon()}
          {showAddToWatchListText && <span className="font-normal max-sm:hidden">{isActive ? 'Remove from Watch List' : 'Add to Watch List'}</span>}
        </Button>
      );
    }

    return (
      <Button aria-label={`Sign in to add ${movieTitle} to Watch List`}
        className="p-0 h-auto bg-transparent hover:bg-transparent"
        data-test="link-sign-in"
        variant="ghost"
        onClick={handleSignInClick}
      >
        {getIcon()}
        {showAddToWatchListText && <span className="font-normal max-sm:hidden">{isActive ? 'Remove from Watch List' : 'Add to Watch List'}</span>}
      </Button>
    );
  };

  const getIcon = (): React.JSX.Element => {
    const Icon = isActive ? WatchListActiveIcon : WatchListIcon;
    return (
      <Icon aria-hidden="true"
        className={clsx('watch-list-icon', className)}
      />
    );
  };

  return displayAnchorOrAction();
};
